import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { forkJoin, from, of } from "rxjs";
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom
} from "rxjs/operators";
import moment from "moment";
import {
  AvailableTimeSlotsRequest,
  BookingCustomerRequest,
  BookingDamageReportRequest,
  CampaignByCodeRequest,
  Client,
  CreateBookingErrorResponse,
  CreateBookingRequest,
  DealerRequest,
  EbuBookingRequest,
  GetTechUpdateStatusRequest,
  PackageRequest,
  PackageResponse,
  PackageTypeEnum,
  SwaggerException,
  VehicleChassiRequest,
  VehicleSearchRequest
} from "src/domain/client";
import { TrackingService } from "src/app/services/tracking.service";
import { UrlService } from "src/app/services/url.service";
import { CarBrandPipe } from "src/app/pipes/car-brand.pipe";
import * as BookingActions from "./booking.actions";
import { AppError } from "./booking.state";
import { Store } from "@ngrx/store";
import {
  selectAdditionalPackages,
  selectAllSelectedPackagesForRequest,
  selectAvailableTimes,
  selectBasePackages,
  selectBookingState,
  selectCarInfo,
  selectContactInformation,
  selectEnabledServiceTypes,
  selectFileIds,
  selectIsEbuEnabled,
  selectOdometer,
  selectPackagesUsedForTimeRequest,
  selectProviderIds,
  selectSelectedDate,
  selectSelectedDealer,
  selectSelectedDropoffPackage,
  selectSelectedEbuCar,
  selectSelectedTime,
  selectSelectedWorkshopSystems,
  selectWarningLightIds
} from "./booking.selectors";
import { PackageService } from "../services/package.service";
import { PagingService } from "../services/paging.service";
import { BusinessProposalService } from "../services/business-proposal.service";
import { PackageProductMapPipe } from "../pipes/package-product-map.pipe";
import {
  _getEbuBookingDates,
  _getEbuBookingTimes,
  _getResourceRequest,
  _parseCreateBookingError,
  fetchSpecificationsData$
} from "./utils/booking-effects.helper";
import { Features, FeatureService } from "../services/feature.service";
import { DatePipe } from "@angular/common";
import { TotalPricePipe } from "../pipes/total-price.pipe";
import { IProduct } from "../interfaces/gtm-interfaces";

@Injectable()
export class BookingEffects {
  constructor(
    private actions$: Actions,
    private trackingService: TrackingService,
    private urlService: UrlService,
    private carBrandPipe: CarBrandPipe,
    private store: Store,
    private packageService: PackageService,
    private pagingService: PagingService,
    private businessProposalService: BusinessProposalService,
    private packageProductMapPipe: PackageProductMapPipe,
    private featureService: FeatureService,
    private datePipe: DatePipe,
    private totalPricePipe: TotalPricePipe
  ) {}

  // --- Step 1 Effects: About Vehicle ---
  loadCarInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.submitAboutVehicle),
      switchMap((action) =>
        from(
          new Client().apiV2VehicleSearch(
            new VehicleSearchRequest({
              registrationNumber:
                action.chassiNumber.length < 17
                  ? action.chassiNumber
                  : undefined,
              chassiNumber:
                action.chassiNumber.length === 17
                  ? action.chassiNumber
                  : undefined
            })
          )
        ).pipe(
          mergeMap((response) => {
            const brand = this.carBrandPipe.transform(response);

            if (
              this.urlService.brand !== "default" &&
              this.urlService.brand !== brand
            ) {
              const brandMismatchError: AppError = {
                status: 0,
                message: `Vehicle brand (${brand}) does not match page brand (${this.urlService.brand}).`
              };
              return of(
                BookingActions.loadCarInfoFailure({
                  error: brandMismatchError,
                  carInfo: response
                })
              );
            }

            const actions = [];
            actions.push(
              BookingActions.loadCarInfoSuccess({
                carInfo: response,
                searchedByRegno: action.searchedByRegno
              })
            );
            if (this.featureService.isFeatureEnabled(Features.TECH_UPDATE)) {
              actions.push(
                BookingActions.checkTechUpdateStatus({
                  chassiNumber: response.chassiNumber
                })
              );
            }
            return actions;
          }),
          catchError((error) => {
            const serializedError: AppError = {
              status: error.status,
              message: error.message
            };
            return of(
              BookingActions.loadCarInfoFailure({ error: serializedError })
            );
          })
        )
      )
    )
  );

  trackCarSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.loadCarInfoSuccess),
        tap(({ carInfo, searchedByRegno }) => {
          this.trackingService.trackSelectCar(
            searchedByRegno,
            carInfo.modelVariantName,
            carInfo.brandName,
            carInfo.modelYear.toString(),
            "",
            carInfo.hasServiceAgreement,
            carInfo.isCompanyVehicle,
            carInfo.originalVehicleSegment.toString(),
            carInfo.fuelType
          );
        })
      ),
    { dispatch: false }
  );

  checkTechUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.checkTechUpdateStatus),
      mergeMap((action) =>
        from(
          new Client().apiV2VehicleGetTechUpdateStatus(
            new GetTechUpdateStatusRequest({
              chassiNumber: action.chassiNumber
            })
          )
        ).pipe(
          map((response) =>
            BookingActions.loadTechUpdateStatusSuccess({ status: response })
          ),
          catchError((error) =>
            of(BookingActions.loadTechUpdateStatusFailure({ error }))
          )
        )
      )
    )
  );

  trackMileage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.updateOdometer),
        tap(({ odometer }) => {
          this.trackingService.trackMilageSelected(odometer);
        })
      ),
    { dispatch: false }
  );

  // --- Step 2 Effects: Workshop Selection ---
  loadWorkshopSystems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.loadActiveWorkshopSystems),
      withLatestFrom(
        this.store.select(selectCarInfo),
        this.store.select(selectOdometer)
      ),
      filter(([_, carInfo]) => !!carInfo),
      switchMap(([_, carInfo, odometer]) =>
        from(
          new Client().apiV2WorkshopSystemActiveWorkshops(
            new VehicleChassiRequest({
              chassiNumber: carInfo.chassiNumber,
              odometer
            })
          )
        ).pipe(
          map((response) =>
            BookingActions.loadActiveWorkshopSystemsSuccess({
              systems: response
            })
          ),
          catchError((error: SwaggerException) => {
            const serializedError: AppError = {
              status: error.status,
              message: error.message
            };
            return of(
              BookingActions.loadActiveWorkshopSystemsFailure({
                error: serializedError
              })
            );
          })
        )
      )
    )
  );

  trackWorkshopSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.submitWorkshopSystems),
        tap(({ selectedSystems }) => {
          this.trackingService.trackServiceCategoriesChosen(selectedSystems);
        })
      ),
    { dispatch: false }
  );

  trackDealerSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.selectDealer),
        withLatestFrom(this.store.select(selectSelectedDealer)),
        tap(([action, selectedDealer]) => {
          if (selectedDealer) {
            this.trackingService.trackWorkshopChosen(
              selectedDealer,
              action.hasSearched
            );
          }
        })
      ),
    { dispatch: false }
  );

  // --- Step 3 Effects: Find Dealer ---
  loadDealers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.loadDealers),
      withLatestFrom(
        this.store.select(selectCarInfo),
        this.store.select(selectSelectedWorkshopSystems),
        this.store.select(selectProviderIds)
      ),
      filter(([_, carInfo, systems]) => !!carInfo && !!systems),
      switchMap(([action, carInfo, systems, providerIds]) => {
        const dealerNoList = action.loadAll ? null : providerIds;

        return from(
          new Client().apiV2DealerGetAll(
            new DealerRequest({
              dealerNoList: dealerNoList,
              chassiNumber: carInfo.chassiNumber,
              workshopSystemFilter: systems
            })
          )
        ).pipe(
          map((response) =>
            BookingActions.loadDealersSuccess({ dealers: response })
          ),
          catchError((error: SwaggerException) => {
            const serializedError: AppError = {
              status: error.status,
              message: error.message
            };
            return of(
              BookingActions.loadDealersFailure({ error: serializedError })
            );
          })
        );
      })
    )
  );

  loadEnabledServiceTypesOnDealerChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.selectDealer),
      withLatestFrom(
        this.store.select(selectCarInfo),
        this.store.select(selectOdometer),
        this.store.select(selectSelectedWorkshopSystems)
      ),
      filter(
        ([_, carInfo, odometer, systems]) =>
          !!carInfo && !!odometer && !!systems
      ),
      switchMap(([action, carInfo, odometer, systems]) =>
        this.packageService
          .refreshEnabledServiceTypes({
            chassiNumber: carInfo.chassiNumber,
            dealerNumber: action.dealerNumber,
            odometer: odometer * 10,
            systems: systems
          })
          .pipe(
            map((types) =>
              BookingActions.loadEnabledServiceTypesSuccess({ types })
            ),
            catchError((error: SwaggerException) => {
              const serializedError: AppError = {
                status: error.status,
                message: error.message
              };
              return of(
                BookingActions.loadEnabledServiceTypesFailure({
                  error: serializedError
                })
              );
            })
          )
      )
    )
  );

  // --- Step 4 Effects: Choose Specifications ---

  loadSpecificationsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.loadSpecificationsPageData),
      withLatestFrom(
        this.store.select(selectCarInfo),
        this.store.select(selectSelectedDealer),
        this.store.select(selectSelectedWorkshopSystems),
        this.store.select(selectOdometer),
        this.store.select(selectEnabledServiceTypes)
      ),
      filter(
        ([_, carInfo, selectedDealer, systems, odometer]) =>
          !!carInfo && !!selectedDealer && !!systems && odometer !== null
      ),
      switchMap(
        ([
          _,
          carInfo,
          selectedDealer,
          systems,
          odometer,
          enabledServiceTypes
        ]) =>
          fetchSpecificationsData$(
            this.packageService,
            this.businessProposalService,
            carInfo,
            selectedDealer,
            systems,
            odometer,
            enabledServiceTypes
          ).pipe(
            map((results) =>
              BookingActions.loadSpecificationsPageDataSuccess(results)
            ),

            catchError((error: SwaggerException) => {
              const serializedError: AppError = {
                status: error.status,
                message: error.message
              };
              return of(
                BookingActions.loadSpecificationsPageDataFailure({
                  error: serializedError
                })
              );
            })
          )
      )
    )
  );

  reloadPackagesOnSystemChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.submitWorkshopSystems),
      withLatestFrom(this.store.select(selectBasePackages)),
      filter(([_, basePackages]) => !!basePackages),
      map(() =>
        BookingActions.loadSpecificationsPageData({ isPartialReload: true })
      )
    )
  );

  trackListView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.loadBasePackagesSuccess),
        withLatestFrom(this.store.select(selectCarInfo)),
        tap(([{ packages }, carInfo]) => {
          if (!packages || !carInfo) return;
          const filteredPackages = packages.filter(
            (x) =>
              x.type !== PackageTypeEnum.CheckInOption &&
              x.type !== PackageTypeEnum.DropoffOption &&
              x.type !== PackageTypeEnum.Mobility
          );

          const products = filteredPackages.flatMap((x) => [
            this.packageProductMapPipe.transform(x, carInfo.brandName),
            ...x.subPackages.map((sp) =>
              this.packageProductMapPipe.transform(sp, carInfo.brandName)
            )
          ]);

          this.trackingService.trackItemListViewed("Step4", products);
        })
      ),
    { dispatch: false }
  );

  trackPackageSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.updateSelectedPackages),
        debounceTime(1500),
        withLatestFrom(this.store.select(selectBasePackages)),
        tap(([{ selectedPackages }, basePackages]) => {
          if (!basePackages || !selectedPackages) return;
          const selectedPackageIds = Object.values(selectedPackages as object)
            .filter((formPart) => formPart && formPart.selectedContainer)
            .map((formPart) => formPart.selectedContainer);

          const matchingPackages = basePackages.filter((pkg) =>
            selectedPackageIds.includes(pkg.id)
          );

          const matchingSubPackages = matchingPackages.flatMap((p) => {
            const formPart = Object.values(selectedPackages as object).find(
              (fp) => fp.selectedContainer === p.id
            );
            if (!formPart || !formPart[p.id]) return [];

            const subPackageControls = formPart[p.id];
            return p.subPackages.filter(
              (sp) => subPackageControls[sp.id] === true
            );
          });

          this.trackingService.trackServiceTypeChosen(
            true,
            matchingPackages,
            matchingSubPackages as PackageResponse[]
          );
        })
      ),
    { dispatch: false }
  );

  // --- Step 5 Effects: Extra Services ---
  loadExtraServices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.loadExtraServicesData),
      withLatestFrom(
        this.store.select(selectCarInfo),
        this.store.select(selectSelectedDealer),
        this.store.select(selectOdometer)
      ),
      filter(
        ([_, carInfo, selectedDealer, odometer]) =>
          !!carInfo && !!selectedDealer && odometer !== null
      ),
      switchMap(([_, carInfo, selectedDealer, odometer]) => {
        const client = new Client();
        const requestData = {
          chassiNumber: carInfo.chassiNumber,
          dealerNumber: selectedDealer.number,
          odometer: odometer * 10
        };

        const washAndRecond$ = from(
          client.apiV2PackageWashAndRecond(new PackageRequest(requestData))
        ).pipe(catchError(() => of([])));
        const dealerExtra$ = from(
          client.apiV2PackageDealerExtra(new PackageRequest(requestData))
        ).pipe(catchError(() => of([])));

        return forkJoin([washAndRecond$, dealerExtra$]).pipe(
          map(([wash, dealer]) => {
            const extraPackages = [...wash, ...dealer];
            return BookingActions.loadExtraServicesDataSuccess({
              packages: extraPackages
            });
          }),
          catchError((error: SwaggerException) => {
            const serializedError: AppError = {
              status: error.status,
              message: error.message
            };
            return of(
              BookingActions.loadExtraServicesDataFailure({
                error: serializedError
              })
            );
          })
        );
      })
    )
  );

  trackExtraServicesListView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.loadExtraServicesDataSuccess),
        withLatestFrom(this.store.select(selectCarInfo)),
        tap(([{ packages }, carInfo]) => {
          if (!packages || !carInfo) return;

          const products = packages.flatMap((pkg) => [
            this.packageProductMapPipe.transform(pkg, carInfo.brandName),
            ...pkg.subPackages.map((sp) =>
              this.packageProductMapPipe.transform(sp, carInfo.brandName)
            )
          ]);

          this.trackingService.trackItemListViewed("Step5", products);
        })
      ),
    { dispatch: false }
  );

  // --- Step 5 Effects: Time Selection

  loadAvailableTimes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.loadAvailableTimes),
      withLatestFrom(
        this.store.select(selectCarInfo),
        this.store.select(selectSelectedDealer),
        this.store.select(selectOdometer),
        this.store.select(selectAllSelectedPackagesForRequest)
      ),
      filter(
        ([_, carInfo, selectedDealer, odometer, selectedPackages]) =>
          !!carInfo &&
          !!selectedDealer &&
          odometer !== null &&
          !!selectedPackages
      ),
      switchMap(([_, carInfo, selectedDealer, odometer, selectedPackages]) =>
        from(
          new Client().apiV2CalendarAvailableTimeSlots(
            undefined,
            AvailableTimeSlotsRequest.fromJS({
              dealerNumber: selectedDealer.number,
              chassiNumber: carInfo.chassiNumber,
              odometer: odometer * 10,
              selectedPackages: selectedPackages
            })
          )
        ).pipe(
          map((response) =>
            BookingActions.loadAvailableTimesSuccess({
              times: response,
              packagesUsedForRequest: selectedPackages
            })
          ),
          catchError((error: SwaggerException) => {
            const serializedError: AppError = {
              status: error.status,
              message: error.message
            };
            return of(
              BookingActions.loadAvailableTimesFailure({
                error: serializedError
              })
            );
          })
        )
      )
    )
  );

  loadEbuCars$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BookingActions.selectBookingDate,
        BookingActions.selectBookingTime,
        BookingActions.selectDropoffOption
      ),
      withLatestFrom(
        this.store.select(selectSelectedDate),
        this.store.select(selectSelectedTime),
        this.store.select(selectSelectedDealer),
        this.store.select(selectIsEbuEnabled)
      ),
      filter(
        ([_, selectedDate, __, selectedDealer, ebuEnabled]) =>
          !!selectedDate && !!selectedDealer && ebuEnabled
      ),
      switchMap(([_, selectedDate, selectedTime, selectedDealer]) => {
        const date = moment(selectedDate).format("YYYY-MM-DD");
        const { startTime, endTime } = _getEbuBookingTimes(
          selectedDealer,
          selectedDate,
          selectedTime
        );

        return from(
          new Client().apiV2EuropcarAvailableCars(
            selectedDealer.number,
            date,
            startTime,
            endTime
          )
        ).pipe(
          map((cars) => BookingActions.loadEbuCarsSuccess({ cars })),
          catchError((error: SwaggerException) => {
            const serializedError: AppError = {
              status: error.status,
              message: error.message
            };
            return of(
              BookingActions.loadEbuCarsFailure({
                error: serializedError
              })
            );
          })
        );
      })
    )
  );

  trackTimeSelectionListView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.loadAvailableTimesSuccess),
        withLatestFrom(
          this.store.select(selectBasePackages),
          this.store.select(selectCarInfo)
        ),
        tap(([_, basePackages, carInfo]) => {
          if (!basePackages || !carInfo) return;
          const packages = basePackages.filter(
            (x) =>
              x.type === PackageTypeEnum.CheckInOption ||
              x.type === PackageTypeEnum.DropoffOption ||
              x.type === PackageTypeEnum.Mobility
          );
          const products = packages.flatMap((x) => [
            this.packageProductMapPipe.transform(x, carInfo.brandName),
            ...x.subPackages.map((sp) =>
              this.packageProductMapPipe.transform(sp, carInfo.brandName)
            )
          ]);
          this.trackingService.trackItemListViewed("Step6", products);
        })
      ),
    { dispatch: false }
  );

  trackDateSelected$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.selectBookingDate),
        withLatestFrom(this.store.select(selectSelectedDropoffPackage)),
        filter(([{ date }, dropoffOption]) => !!date && !!dropoffOption),
        tap(([{ date }, dropoffOption]) => {
          this.trackingService.trackDateSelected(
            new Date(date),
            dropoffOption.description?.header,
            dropoffOption.subPackages.map((x) => x.name)
          );
        })
      ),
    { dispatch: false }
  );

  // --- Step 7 Effects: Contact Information ---

  validateCampaignCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.validateCampaignCode),
      withLatestFrom(this.store.select(selectSelectedDealer)),
      filter(([_, selectedDealer]) => !!selectedDealer),
      switchMap(([action, selectedDealer]) =>
        from(
          new Client().apiV2DealerGetCampaignCode(
            new CampaignByCodeRequest({
              dealerNo: selectedDealer.number,
              campaignCode: action.code
            })
          )
        ).pipe(
          map((response) =>
            BookingActions.validateCampaignCodeSuccess({
              response: response ?? null
            })
          ),
          catchError((error: SwaggerException) => {
            const serializedError: AppError = {
              status: error.status,
              message: error.message
            };
            return of(
              BookingActions.validateCampaignCodeFailure({
                error: serializedError
              })
            );
          })
        )
      )
    )
  );

  trackContactFormStarted$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.contactInfoPageLoaded),
        withLatestFrom(this.store.select(selectContactInformation)),
        filter(([_, contactInfo]) => !contactInfo?.firstName),
        tap(() => {
          this.trackingService.trackContactFormStarted();
        })
      ),
    { dispatch: false }
  );

  trackContactFormSubmitted$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.submitContactInformation),
        withLatestFrom(this.store.select(selectContactInformation)),
        filter(([_, contactInfo]) => !!contactInfo),
        tap(([_, contactInfo]) => {
          this.trackingService.trackContactFormSubmitted(
            contactInfo.smsChecked
          );
        })
      ),
    { dispatch: false }
  );

  // --- Step 8 Effects: Complete Booking ---

  submitBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.submitBooking),
      withLatestFrom(
        this.store.select(selectBookingState),
        this.store.select(selectAllSelectedPackagesForRequest),
        this.store.select(selectFileIds),
        this.store.select(selectWarningLightIds),
        this.store.select(selectSelectedEbuCar),
        this.store.select(selectSelectedDealer)
      ),
      switchMap(
        ([
          _,
          state,
          allSelectedPackagesForRequest,
          fileIds,
          warningLightIds,
          selectedEbuCar,
          selectedDealer
        ]) => {
          const customer = new BookingCustomerRequest({
            firstName: state.contactInformation.firstName,
            lastName: state.contactInformation.lastName,
            email: state.contactInformation.email,
            message: state.contactInformation.other,
            phone: state.contactInformation.phone,
            sendSms: state.contactInformation.smsChecked || false
          });

          let damageReport: BookingDamageReportRequest | null = null;
          const windscreenForm = state.selectedPackages?.windscreenForm;
          if (windscreenForm?.insurance && windscreenForm?.date) {
            damageReport = new BookingDamageReportRequest({
              damageDate: new Date(windscreenForm.date),
              insuranceCompanyId: parseInt(windscreenForm.insurance),
              description: windscreenForm.damage || null,
              location: windscreenForm.place || null,
              reason: windscreenForm.reason || null
            });
          }

          const bookingTime = new Date(state.selectedDate);
          const bookingResource = _getResourceRequest(state, this.datePipe);
          const allSelectedPackages = allSelectedPackagesForRequest;
          const ebuBookingDates = selectedEbuCar
            ? _getEbuBookingDates(
                selectedDealer,
                state.selectedDate,
                state.selectedTime
              )
            : null;

          const bookingRequest = new CreateBookingRequest({
            chassiNumber: state.carInfo.chassiNumber,
            odometer: state.odometer * 10,
            dealerNumber: state.selectedDealerNumber,
            bookingCustomer: customer,
            bookingDamageReport: damageReport,
            time: bookingTime,
            usesTimeSlot: bookingResource?.usesTimeSlot,
            selectedPackages: allSelectedPackages,
            fileIds,
            campaignCode: state.campaignResponse?.name ?? null,
            warningLightIds,
            customerMessage: customer.message || state.selectedPackages?.other,
            troubleShootingMessage:
              state.selectedPackages?.troubleshootingForm?.helpText,
            bookingResource,
            successfulRecallCheck:
              state.techUpdateStatus?.successfulRecallCheck,
            businessProposalId:
              this.businessProposalService.businessProposal$.value
                ?.businessProposalId,
            ebuBooking: selectedEbuCar
              ? new EbuBookingRequest({
                  carGroup: selectedEbuCar.groupID,
                  priceExVat: selectedEbuCar.priceExVat,
                  priceIncVat:
                    selectedEbuCar.priceIncVat ?? selectedEbuCar.previewValue,
                  startDate: ebuBookingDates?.startDate,
                  endDate: ebuBookingDates?.endDate
                })
              : undefined
          });

          return from(new Client().apiV2Booking(bookingRequest)).pipe(
            map((response) =>
              BookingActions.submitBookingSuccess({ orderId: response.orderId })
            ),
            catchError(
              (reason: CreateBookingErrorResponse | SwaggerException) => {
                const error = _parseCreateBookingError(reason);
                return of(BookingActions.submitBookingFailure({ error }));
              }
            )
          );
        }
      )
    )
  );

  trackBookingSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BookingActions.submitBookingSuccess),
        withLatestFrom(
          this.store.select(selectAllSelectedPackagesForRequest),
          this.store.select(selectBasePackages),
          this.store.select(selectAdditionalPackages),
          this.store.select(selectCarInfo),
          this.store.select(selectContactInformation)
        ),
        tap(
          ([
            action,
            selectedPackages,
            basePackages,
            additionalPackages,
            carInfo,
            contactInfo
          ]) => {
            const allAvailablePackages = [
              ...(basePackages ?? []),
              ...(additionalPackages ?? [])
            ];
            const products: IProduct[] = [];
            selectedPackages.forEach((reqPkg) => {
              const pkg = allAvailablePackages.find(
                (p) => p.id === reqPkg.packageId?.toString()
              );
              if (pkg) {
                products.push(
                  this.packageProductMapPipe.transform(pkg, carInfo.brandName)
                );
                reqPkg.subPackageIds?.forEach((subId) => {
                  const subPkg = pkg.subPackages.find((sp) => sp.id === subId);
                  if (subPkg)
                    products.push(
                      this.packageProductMapPipe.transform(
                        subPkg,
                        carInfo.brandName
                      )
                    );
                });
              }
            });

            this.trackingService.trackPurchaseCompleteEcom(
              Math.floor(this.totalPricePipe.transform(selectedPackages)),
              products,
              action.orderId,
              contactInfo.discountCode
            );
          }
        )
      ),
    { dispatch: false }
  );

  // --- Shared Effects ---

  handleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          BookingActions.loadCarInfoFailure,
          BookingActions.loadActiveWorkshopSystemsFailure,
          BookingActions.loadDealersFailure,
          BookingActions.loadSpecificationsPageDataFailure,
          BookingActions.loadExtraServicesDataFailure
        ),
        tap(({ error }) => {
          console.error("A critical API error occurred:", error);
          if (error.status === 404 || error.status === 503) {
            this.pagingService.navigateToServiceUnavailable();
          }
        })
      ),
    { dispatch: false }
  );

  invalidateTimesOnPackageChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.updateSelectedPackages),
      withLatestFrom(
        this.store.select(selectAllSelectedPackagesForRequest),
        this.store.select(selectPackagesUsedForTimeRequest),
        this.store.select(selectAvailableTimes)
      ),
      filter(
        ([action, currentPackages, usedPackages, availableTimes]) =>
          !action.selectedPackages.timeSelectionForm
      ),
      filter(
        ([action, currentPackages, usedPackages, selectAvailableTimes]) =>
          usedPackages !== null && selectAvailableTimes !== null
      ),
      filter(
        ([action, currentPackages, usedPackages]) =>
          JSON.stringify(currentPackages) !== JSON.stringify(usedPackages)
      ),
      mergeMap(() => [
        BookingActions.clearStateFromStep({ stepNumber: 6 }),
        BookingActions.updateSelectedPackages({
          selectedPackages: { timeSelectionForm: null }
        })
      ])
    )
  );
}
